import type { BrowserHistoryBuildOptions } from 'history';

import { createMemoryHistory, createBrowserHistory } from 'history';

import getSessionKey from '@core/utils/getSessionKey';
import { getIframePreviewFlags } from '@core/utils/iframe';

/**
 * The key for the property that contains the shared history object for React Router.
 */
export const globalHistory = `@readmeHistory-${getSessionKey()}`;

/**
 * Creates a history object that is shared between the top frame router
 * and the router in the iframe preview app. This allows us to
 * synchronize the history between the two frames, so that when a user
 * navigates in the iframe, the top frame's router also updates and vice versa.
 *
 * @example
 * <Browser history={crossFrameHistory()}>
 */
export default function crossFrameHistory(options: BrowserHistoryBuildOptions) {
  const isClient = typeof window !== 'undefined';
  const { isIframe, isIframePreview } = getIframePreviewFlags();

  // https://bin.readme.com/ will load an iframe with the Hub app from 'preview.readme.io'
  // In this case we don't want to share the history across frames.
  const isBinPreviewSite = process.env.NODE_ENV === 'playground';

  // Skip this function if we are not in a browser environment.
  if (!isClient) {
    return createMemoryHistory();
  }

  // If we are in a bin preview site, create a new history object without
  // sharing it across frames.
  if (isBinPreviewSite || (isIframe && !isIframePreview)) {
    return createBrowserHistory(options);
  }

  // If we are in an iframe, return the history object from the parent window.
  // If it doesn't exist, create a new one and store it in the parent window.
  if (isIframePreview) {
    window.parent[globalHistory] ??= createBrowserHistory(options);
    return window.parent[globalHistory];
  }

  // If we are in a parent window, create a new history object and tack it to the global
  // `window` object so that it can be accessed by child iframes.
  window[globalHistory] ??= createBrowserHistory(options);
  return window[globalHistory];
}
